import config from "appConfig";

const theory = `<p>In the context of linear acoustics, the superposition principle can be applied. So, in the presence of two sources, the pressure fields generated by each source can be added. However this is not so straightforward in practice as sources can interfere and may be coupled. In the following various situations are considered showing that this question is not as simple as we might think.
<h3 class=sectionHead>Sources of same frequency</h3>
<p>
   Let consider two sources delivering pure waves of same frequency which pressure complex amplitudes at a given point are
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>. In that case, the resulting pressure complex is simply 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>.
<h4 class=subsectionHead>Sources in phase</h4>
<p>
   If
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are in phase the RMS value is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mo class=MathClass-rel>|</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
   </math>. If e.g.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>, the pressure amplitude is multiplied by a factor of two and the pressure level is increased by 6 dB. This situation occurs when a source is placed near a rigid wall: at low frequencies, i.e. when the distance between the source and the wall is small compared to the wavelength, the image source is in phase with the source and the pressure amplitude is doubled. This can be related to the fact that when a source is placed near a wall, the radiation impedance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   is multiplied by a factor of two, which also implies that for a given volume velocity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>U</mi>
   </math>
   the pressure is twice larger as well as the power as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mi mathvariant=italic>Re</mi>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>|</mo>
      <mi>U</mi>
      <msup>
         <mrow>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi mathvariant=italic>Re</mi>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>|</mo>
      <mi>p</mi>
      <msup>
         <mrow>
            <mo class=MathClass-rel>|</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>. This situation implies that the acoustic intensity is multiplied by a factor of four which means that the energy delivered by the source is multiplied by a factor of two. This is only possible if the source is able to deliver a twice larger acoustic power which is the case if the source impedance (in a Thevenin model) is large compared to the radiation impedance.
<h4 class=subsectionHead>Sources out of phase</h4>
<p>
   If
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   are out of phase the RMS value is given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>|</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mo class=MathClass-rel>|</mo>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>|</mo>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jϕ</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>
   is the phase shift between
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>. In that case the resulting RMS pressure lies between
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>+</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
   </math>
   when pressures are in phase and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>|</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>|</mo>
   </math>
   when pressures are in phase opposition. When pressure are in phase this corresponds to what is called constructive interferences and when pressure are opposite in phase this corresponds to destructive interferences. An interesting case is when the two pressures are in quadrature. They are orthogonal in the sense of the Fourier transform and in that case
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mo class=MathClass-rel>|</mo>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>|</mo>
                  <mo class=MathClass-bin>+</mo>
                  <mi>j</mi>
                  <mo class=MathClass-rel>|</mo>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>|</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-bin>+</mo>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>. In this situation the acoustic intensity adds instead of pressure amplitude when pressure are in phase. So, if, for example,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
   </math>
   pressure level is increased by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
   </math> dB. Considering two close sources, in most cases the radiated power is not the sum of respective power of the sources. When in phase, considering to identical sources the total power quadrupled. When in quadrature the power is the sum of the power of each source. When in phase opposition the power of both sources cancel each other as in the case of the dipole.
<h3 class=sectionHead>Uncorrelated sources</h3>
<p>
   When sources involves non pure signals superposition principle is still valid but the calculation off the pressure level or power depends on the correlation between the signals. If the source signals are identical the rules for sources in phase can be applied. If signals are uncorrelated, intensities can be added. In terms of RMS pressure, it can be written
   When sources involves non pure signals superposition principle is still valid but the calculation off the pressure level or power depends on the correlation between the signals. If the source signals are identical the rules for sources in phase can be applied. If signals are uncorrelated, intensities can be added. In terms of RMS pressure, it can be written
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>RMS</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mi mathvariant=italic>RMS</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-bin>+</mo>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>RMS</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </mrow>
      </msqrt>
   </math>. So, two uncorrelated sources delivering the same pressure level results in a pressure level
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
   </math> dB higher as for sources in quadrature. This is the hypothesis which is in general retained in the case of complex signals even if the sources are correlated because, due to the propagation some frequencies may be found in phase while other will be out of phase. Statistically, considering octave bands, considering sources uncorrelated will roughly give the correct sound pressure level, except possibly in the firsts octave bands.
<h3 class=sectionHead>Sources near a wall</h3>
<p>
   The case of a source placed near a wall is a twisted situation. Indeed considering a source near a wall suggest to consider an image source in the wall. Then, at low frequency both sources may be in phase, which results in a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
      <mn>6</mn>
   </math> dB of the sound pressure level. But in practice the low frequency level range will be rather limited. Let consider a usual woofer (low frequency acoustic speaker), which thickness is in the range of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>50</mn>
   </math> cm. The distance between the speaker and its image is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math> m, which, for a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>100</mn>
   </math> Hz frequency, may result in a phase shift of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>8</mn>
   </math>
   radians which is close to the quadrature. So, in this example, for frequencies higher than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>100</mn>
   </math> Hz, considering the uncorrelated situation is reasonable. For lower frequencies the in-phase situation might be preferred. Obviously this depends on the distance between the source and the wall. When this distance is larger than a quarter wave length, the high frequency situation has to be considered. When this distance is lower than the eighth of a wave length, the low frequency situation has to be considered. A practical consequence of that is that when placing a source near a wall or in a corner, the frequency response is modified, low frequencies being much more reinforced than high frequencies. For example for a source in a corner this can be
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
      <mn>18</mn>
   </math> dB for very low frequencies against
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-bin>+</mo>
      <mn>9</mn>
   </math> dB for medium and high frequencies.
<h3 class=sectionHead>Adding source power using dB scale</h3>
<p>It can be useful to be able to add source powers without using a calculator. For that it is useful to know the power resulting from two sources differing from few decibels. This is given in <a href="#" data-img-alt="Table 1. Summation of two sources, first one being fixed arbitrarily to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline> <mn>0</mn></math> dB." data-img-src=${config.ASSETS_URL}/01/23/theory/table-1.svg>Table 1</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>
         Table 1. Summation of two sources, first one being fixed arbitrarily to
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mn>0</mn>
            </math> dB.
      </figcaption>
      <img alt="Table 1. Summation of two sources, first one being fixed arbitrarily to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline> <mn>0</mn></math> dB." src=${config.ASSETS_URL}/01/23/theory/table-1.svg>
   </figure>
</div>
<h3 class=likesectionHead>Glossary</h3>
<p>
<div class=supertabular>
    <dl>
        <dt>RMS</dt>
        <dd>Root Mean Square</dd>
    </dl>
</div>           
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt>[1]
   <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
   <dt>[2]
   <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
   <dt>[3]
   <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
</dl>`;

export default theory;
